import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, REFRAME, OVERLAP, MOAT, PROOF, GATEWAY, HONESTY, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* honest status tag — used on the gateway scene */
const StatusTag: React.FC<{ label: string; color: string }> = ({ label, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 16,
      letterSpacing: "0.2em",
      textTransform: "uppercase",
      color,
      border: `1px solid ${color}`,
      borderRadius: 999,
      padding: "5px 14px",
      background: `${color}14`,
    }}
  >
    {label}
  </div>
);

/* 01 — HOOK : the founder's fear */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const fade = interpolate(frame, [44, 78], [0.3, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="opinion · positioning" color={COLORS.amber} />
          <Big>
            A big lab ships a{" "}
            <span style={{ color: COLORS.amber }}>no-code AI worker.</span>
            <br />
            Doesn't that{" "}
            <span style={{ color: COLORS.red, opacity: fade }}>kill my product?</span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="amber" />
    </AbsoluteFill>
  );
};

/* 02 — REFRAME : the phone vs the memory */
export const Scene02Reframe: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.reframe.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the reframe" color={COLORS.cyan} />
        <div style={{ display: "flex", gap: 44, alignItems: "center" }}>
          <Big size={70}>
            <span style={{ color: COLORS.dim }}>the worker is</span>
            <br />
            the PHONE
          </Big>
          <div style={{ fontFamily: FONTS.display, fontSize: 60, color: COLORS.line }}>│</div>
          <Big size={70}>
            <span style={{ color: COLORS.dim }}>my product is</span>
            <br />
            <span style={{ color: COLORS.cyan }}>the MEMORY</span>
          </Big>
        </div>
        <div
          style={{
            fontFamily: FONTS.body,
            fontSize: 30,
            color: COLORS.dim,
            marginTop: 34,
            maxWidth: 1200,
            textAlign: "center",
          }}
        >
          don't fight the phone — be the brain it calls into
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={REFRAME} accent="cyan" />
  </AbsoluteFill>
);

/* 03 — OVERLAP : where each structurally lives */
const Row: React.FC<{ children: React.ReactNode; dim?: boolean }> = ({ children, dim }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 21,
      lineHeight: 2.1,
      color: dim ? COLORS.dim : COLORS.text,
    }}
  >
    {children}
  </div>
);
export const Scene03Overlap: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.overlap.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="looks like overlap · isn't" color={COLORS.violet} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
          <Panel accent="amber" title="no-code worker · the phone" width={560}>
            <Row dim>single-user · one laptop</Row>
            <Row>plugin = folder + Markdown</Row>
            <Row>MCP = edit a local JSON file</Row>
            <Row dim>brain = files you maintain by hand</Row>
          </Panel>
          <Panel accent="cyan" title="EAKC · the memory" width={560}>
            <Row dim>multi-tenant · hosted · audited</Row>
            <Row>
              skill = row in <span style={{ color: COLORS.cyan }}>skills</span> table
            </Row>
            <Row>server-side MCP · governed</Row>
            <Row dim>brain = graph + pgvector, auto-synced</Row>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OVERLAP} accent="violet" />
  </AbsoluteFill>
);

/* 04 — MOAT : three things a folder can't do */
export const Scene04Moat: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [20, 50], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c2 = interpolate(frame, [70, 100], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c3 = interpolate(frame, [120, 150], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.moat.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="the moat · not the chat box" color={COLORS.green} />
        <div style={{ display: "flex", gap: 26 }}>
          <div style={{ opacity: c1 }}>
            <Callout label="graph/drift-detector.ts" accent="cyan" width={360}>
              re-checks knowledge <b style={{ color: COLORS.text }}>on every sync</b>
            </Callout>
          </div>
          <div style={{ opacity: c2 }}>
            <Callout label="verify-output · gate" accent="green" width={360}>
              <b style={{ color: COLORS.text }}>blocks</b> ungrounded answers before they ship
            </Callout>
          </div>
          <div style={{ opacity: c3 }}>
            <Callout label="skills · mig 204" accent="lime" width={360}>
              state machine: <b style={{ color: COLORS.text }}>proposed → active → pinned</b>
            </Callout>
          </div>
        </div>
        <div
          style={{
            fontFamily: FONTS.body,
            fontSize: 28,
            color: COLORS.dim,
            marginTop: 40,
            maxWidth: 1300,
            textAlign: "center",
          }}
        >
          a folder-on-a-laptop has none of these
        </div>
      </AbsoluteFill>
      <Caption lines={MOAT} accent="green" />
    </AbsoluteFill>
  );
};

/* 05 — PROOF : already built */
const PIPE: PipeNode[] = [
  { label: "clients", value: "ANY", sub: "Code · Cursor · GPT", accent: "amber" },
  { label: "protocol", value: "MCP", sub: "/api/mcp", accent: "cyan" },
  { label: "tools", value: "14 INBOUND", sub: "query · graph · docs", accent: "green" },
  { label: "brain", value: "KG + pgvector", sub: "auto-synced", accent: "violet" },
];
export const Scene05Proof: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.proof.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
      <Kicker text="not a roadmap · src/app/api/mcp/route.ts" color={COLORS.green} />
      <div style={{ width: 1680 }}>
        <PipelineGraph nodes={PIPE} startFrame={8} stepFrames={24} nodeWidth={230} />
      </div>
      <div
        style={{
          fontFamily: FONTS.mono,
          fontSize: 20,
          color: COLORS.dim,
          marginTop: 46,
          letterSpacing: "0.04em",
        }}
      >
        connectors: github · gmail · slack · jira · confluence · drive · youtube
      </div>
    </AbsoluteFill>
    <Caption lines={PROOF} accent="green" />
  </AbsoluteFill>
);

/* 06 — GATEWAY : the move + honest QUEUED tag */
const GW = `# eakc-brain.md  —  the front door (QUEUED, not shipped)
# one router file per tenant; any AI client reads it first.

brain:
  query:   mcp://eakc/query_eakc      # SHIPPED
  graph:   mcp://eakc/graph_query     # SHIPPED
  sources: mcp://eakc/list_sources    # SHIPPED

departments:        # Domain Packs — SPEC ONLY (not yet code)
  - marketing
  - sales
  - ops`;
export const Scene06Gateway: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.gateway.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 24 }}>
      <div style={{ display: "flex", gap: 14, marginBottom: 18 }}>
        <StatusTag label="the move" color={COLORS.cyan} />
        <StatusTag label="packaging · NOT YET SHIPPED" color={COLORS.amber} />
      </div>
      <CodePanel
        code={GW}
        language="yaml"
        filename="eakc-brain.md  ·  QUEUED"
        highlightLines={[4, 5, 6]}
        revealFrames={260}
        fontSize={23}
        width={1180}
      />
    </AbsoluteFill>
    <Caption lines={GATEWAY} accent="amber" />
  </AbsoluteFill>
);

/* 07 — HONESTY : shipped vs queued */
export const Scene07Honesty: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.honesty.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="building in public · the honest line" color={COLORS.lime} />
        <div style={{ display: "flex", gap: 30, alignItems: "stretch" }}>
          <Panel accent="green" title="shipped · in the repo" width={560}>
            <Row>MCP server · 14 inbound tools</Row>
            <Row>output gate · verify-output.ts</Row>
            <Row>drift detector · skills (mig 204)</Row>
            <Row>7 connectors · knowledge graph</Row>
          </Panel>
          <Panel accent="amber" title="queued · spec only" width={560}>
            <Row dim>eakc-brain.md router per tenant</Row>
            <Row dim>Domain Packs as "departments"</Row>
            <Row dim>hosted-brain landing experience</Row>
            <Row dim>connect-from-anywhere onboarding</Row>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={HONESTY} accent="lime" />
  </AbsoluteFill>
);

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={66}>
          The no-code worker is the phone.
          <br />
          <span style={{ color: COLORS.cyan }}>Be the memory it calls into.</span>
        </Big>
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 24,
            letterSpacing: "0.24em",
            color: COLORS.dim,
            marginTop: 44,
          }}
        >
          EAKC · the company brain over MCP
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
